/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import {
  findBy,
  mapBy,
  resolved,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';

export default class UnbookedStudentManager extends Component {
  @tracked
  dialogOpen = false;
  event = null;
  filters = [];
  @tracked
  selected = [];
  @tracked teacherId = '';

  @computed('event.name')
  get title() {
    return `Students Without Bookings - ${this.event.name}`;
  }

  @computed(
    'event.{bookings.content.@each.student_id,teacherGroupMappings.[]}',
    'teacherId',
  )
  get remainingStudents() {
    let eventBookings = resolved(this.event.bookings);
    let bookedStudentIds = [
      ...new Set(eventBookings.map((booking) => booking.student_id)),
    ];
    let totalStudents = uniqBy(
      this.event.teacherGroupMappings.reduce((learners, mapping) => {
        if (
          isEmpty(this.teacherId) ||
          mapping.teacherIds.includes(this.teacherId)
        ) {
          return mapping.groups.reduce((ids, group) => {
            ids.push(...mapBy(group.currentLearnerMembers, 'person'));
            return ids;
          }, learners);
        } else {
          return learners;
        }
      }, []),
      'id',
    );
    return totalStudents.filter((learner) => {
      return !bookedStudentIds.includes(learner.id);
    });
  }

  @computed('remainingStudents.[]', 'filters.@each.{field,value}')
  get filteredRemainingStudents() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.remainingStudents;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.remainingStudents.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  openDialog() {
    this.dialogOpen = true;
    this.selected = [];
  }

  @action
  closeDialog() {
    this.dialogOpen = false;
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((p) => p !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleAll() {
    if (this.selected.length === this.get('filteredRemainingStudents.length')) {
      // All already selected; clear selection.
      this.selected = [];
    } else {
      // Not all selected; select them all.
      let selected = this.selected;
      this.filteredRemainingStudents.forEach((learner) => {
        if (!selected.includes(learner)) {
          selected = [...selected, learner];
        }
      });
      this.selected = selected;
    }
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      // Uses set() rather than plain assignment: `filters` is a classic
      // (non-@tracked) field consumed via @computed('filters.@each...'), so
      // only set() notifies that dependent key of the reassignment.
      this.set('filters', [
        ...this.filters,
        {
          field,
          value,
        },
      ]);
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.set(
        'filters',
        this.filters.filter((f) => f !== filter),
      );
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
